import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ArrowRight, Mail, Phone, Shield, Laptop, Users, Globe, FileText, Brain, Star, Lightbulb, Copy, CheckCircle, Calendar, User, Pencil, Lock, Anchor, Hourglass, MessageCircleWarning, Car, BarChart3, Tag, DollarSign, Handshake, Headset, ClipboardList, Puzzle } from 'lucide-react';

const BRAND = {
  navy: '#001F3F',
  red: '#FF4136',
  charcoal: '#1F2937',
  lightGray: '#F8F9FA',
};

const OTDEmailerPage = () => {
  const navigate = useNavigate();
  
  // Email generator state
  const [emailData, setEmailData] = useState({
    year: '',
    make: '',
    model: '',
    trim: '',
    vin: '',
    zip: '',
    purchaseTimeline: '',
    preferredContact: '',
    buyerName: '',
    buyerPhone: '',
    buyerEmail: '',
  });
  const [generatedEmail, setGeneratedEmail] = useState('');
  const [copiedItem, setCopiedItem] = useState('');

  // Helper function to capitalize words properly
  const capitalizeWords = (str) => {
    if (!str) return str;
    return str.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  // Input styling
  const inputClassName = "w-full px-3 py-2.5 rounded-lg text-sm transition-all focus:outline-none focus:ring-2 focus:ring-opacity-50";
  const inputStyle = {
    border: '1px solid #E5E7EB',
    backgroundColor: '#FFFFFF',
    color: '#374151',
  };

  // EXACT phone script copy
  const phoneScript = `Hi, I'm shopping for a [year] [make] [model] and comparing quotes from a few dealerships.

I'm planning to purchase within the next few days and already know the vehicle I want.

I simply need the full out-the-door price — that's the selling price plus taxes, title, registration, and all fees and add-ons — so I can compare offers before coming in.

If the numbers look right, I'm ready to move forward quickly.

I'm local, so service and future business would likely be with your dealership as well.

Could you email me the full OTD price?`;

  // EXACT lead form copy
  const leadFormText = `Hello,

I'm shopping for a [year] [make] [model] and comparing offers from several dealerships.

Could you please send me the full out-the-door price, including taxes, fees, and any required add-ons?

If the numbers make sense, I'm planning to move forward within the next few days.

Thank you.`;

  const generateOTDEmail = () => {
    const year = emailData.year || '[Year]';
    const make = capitalizeWords(emailData.make) || '[Make]';
    const model = capitalizeWords(emailData.model) || '[Model]';
    const trim = capitalizeWords(emailData.trim) || '';
    const vehicleDesc = `${year} ${make} ${model}${trim ? ` ${trim}` : ''}`;
    
    const email = `Subject: Quote Request – ${vehicleDesc}

Hello,

I'm currently comparing offers on a ${vehicleDesc} and would appreciate receiving the full out-the-door price in writing, including:

• Vehicle selling price
• Dealer fees
• Taxes
• Registration/title
• Any required add-ons
• Total out-the-door price
${emailData.vin ? `\nVIN: ${emailData.vin}` : ''}

I am comparing offers from several dealerships and plan to move forward soon with the most competitive written quote.

I'm planning to purchase within the next few days if the numbers make sense.

Thank you for your time.

Best regards
${emailData.buyerName || '[Name]'}
ZIP Code: ${emailData.zip || '[ZIP]'}
${emailData.preferredContact ? `Preferred contact: ${emailData.preferredContact}` : ''}`;

    setGeneratedEmail(email);
  };

  const copyToClipboard = (text, itemName) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(itemName);
    setTimeout(() => setCopiedItem(''), 2000);
  };

  useEffect(() => {
    document.title = 'OTD Quote Emailer – VINdicated.ai';
    const badge = document.getElementById('emergent-badge');
    if (badge) badge.style.display = 'none';
    return () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.style.display = 'flex';
    };
  }, []);

  // Card style helper - floating section container (no left border)
  const cardStyle = (borderColor = BRAND.navy) => ({
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    boxShadow: '0 2px 16px rgba(0, 31, 63, 0.08)',
    border: '1px solid #E5E7EB',
    padding: '24px',
    marginBottom: '24px',
  });

  // Mini card style helper - individual floating cards inside sections
  const miniCardStyle = {
    width: '90%',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: '12px',
    padding: '16px',
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '1px solid #E5E7EB',
    boxShadow: '0 2px 8px rgba(0, 31, 63, 0.08)',
  };

  // EXACT tips copy
  const phoneTips = [
    "Weekday mornings can be easier — stores are often less crowded. Salespeople have more time, and managers are more willing to talk numbers.",
    "Ask for the Internet Sales Manager by name. They handle online and phone quotes daily — they're the ones most comfortable giving you numbers without requiring a visit.",
    "Have a pen ready. Write down who you spoke with, the numbers they gave you, and any conditions. You'll reference this when you compare quotes later.",
    "If they won't give a number over the phone, try this: 'I understand. I'll keep reaching out to other dealerships. If you change your mind, here's my number.' That usually gets a callback.",
    "Do not mention your trade-in when you're shopping for quotes. Dealers use trade-in information to blend the numbers — they'll show you a better price on the new car and quietly make it back on your trade. Lock down the out-the-door price on the new car first. Then — and only then — bring up your trade."
  ];

  // EXACT objection handlers copy
  const objectionHandlers = [
    {
      title: "You Need to Come In",
      response: "I completely understand, but I don't want to spend time driving around just to find out the numbers. I'm ready to move forward soon — I just need to see the out-the-door pricing first so I can compare offers."
    },
    {
      title: "We Don't Want You Shopping Our Numbers",
      response: "I understand, but if I don't have your numbers, the only option I have is to keep contacting other dealerships. I'd prefer to include your dealership in the comparison."
    },
    {
      title: "That's Our Best Price",
      response: "I appreciate that. I'm comparing written quotes from five dealerships this week. If your price is competitive, you'll be at the top of my list. Could you email me the full out-the-door breakdown so I can include you?"
    },
    {
      title: "What's Your Budget?",
      response: "I'd rather focus on the out-the-door price for this specific car first. I'm a serious buyer comparing offers — what can you do on this one?"
    },
    {
      title: "We Can Work Something Out When You Get Here",
      response: "I'm sure we can. I'm narrowing it down to two or three dealerships before I drive out. The ones that send me the numbers in writing are the ones I'll visit first. Can you email me the full OTD breakdown?"
    }
  ];

  // EXACT psychology insights copy
  const psychologyInsights = [
    {
      title: "Anchoring Effect",
      body: "The first number in a negotiation sets the entire range. Asking the dealer for their out-the-door price first means they set the anchor — not you. But here's where it gets powerful: if you've done your homework and know the invoice price and market value — tools like our Market View show you exactly what the dealer paid versus what they're asking — you can counter their anchor with real numbers and logic, not guesses. That completely changes the negotiation."
    },
    {
      title: "Social Proof Pressure",
      body: "When you mention you're comparing five dealerships, the dealer knows they're competing. Competition drives better offers — every time."
    },
    {
      title: "The Written Commitment",
      body: "Once a dealer puts a number in writing — on a buyer's order, a deal sheet, or even an email — it becomes much easier to compare and challenge if numbers change later. Verbal quotes can shift by the time you walk through the door. Written quotes are much easier to compare and challenge if numbers change. Always get it in writing."
    },
    {
      title: "Sunk Cost Effect",
      body: "People keep investing in something just because they've already spent time on it. It's why people stay in bad relationships, finish bad movies, and keep negotiating at a dealership after spending three hours there. Doing 90% of the work from home means you'll never feel trapped into a bad deal just because you drove across town."
    }
  ];

  // EXACT pro moves copy
  const proMoves = [
    "Weekdays are your power window. Dealerships are usually more packed on weekends with less incentive to negotiate. If your schedule allows, weekday mornings can put you in a stronger position.",
    "End of the month matters. Sales teams have monthly, quarterly, and yearly targets. The closer they are to a deadline, the more motivated they are to make a deal work. The last few days of a month are often when the best deals happen.",
    "If a dealer advertises a price online, call about it and get it in writing before you visit. Online prices can change or disappear. A written confirmation before you drive over saves you time and protects your leverage.",
    "Always ask for the out-the-door number. That means the selling price plus taxes, title, registration, all dealer fees, and all add-ons. 'Best price' just means the sticker — you can still get hit with $1,500 to $4,000 or more in extras if you don't ask for the full OTD.",
    "Watch out for pre-installed add-ons. Some dealers add accessories or coatings to the car before you arrive — window tint, paint protection, fabric coating — and then charge you for them. Think of it this way: if you order a cheese pizza and they put pepperoni on it, that's fine, you'll eat it — but you're not paying for the pepperoni. Same idea. If it's already on the car, great. Doesn't mean it goes on your bill.",
    "Get pre-approved through a credit union or your own bank before you contact a single dealer. Walking in with your own financing means the dealer's rate is just an option, not your only option. Even if the dealer ends up beating your rate, you negotiated from strength — not desperation. One thing to keep in mind: most pre-approvals are only good for 30 to 60 days depending on the lender, so time it right. Don't get pre-approved three months before you're ready — do it when you're within a few weeks of buying.",
    "The finance office is where dealers make serious money. Extended warranties, GAP insurance through the dealer, paint protection plans, fabric coating, tire packages — these are marked up significantly. You can almost always buy them cheaper through a third party or your own insurance. Say no in the moment, research later."
  ];

  // EXACT what happens next copy
  const whatHappensNext = [
    "Many dealers respond within 24 to 48 hours to email quote requests; if not, call directly.",
    "If you don't hear back in 48 hours, call them directly using the phone script above.",
    "Some dealers won't email prices — and that tells you something about how they do business.",
    "Compare every quote using the same format: out-the-door price including taxes, title, fees, and all add-ons."
  ];

  return (
    <div 
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{ backgroundColor: '#FFFFFF' }}
      data-testid="otd-emailer-page"
    >
      {/* Navigation Bar */}
      <div 
        className="sticky top-0 z-10"
        style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E8EAED' }}
      >
        <div className="max-w-screen-sm mx-auto px-5 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/public-tools')}
              className="flex items-center gap-2 hover:opacity-70 transition-opacity"
              style={{ color: BRAND.navy }}
              data-testid="otd-emailer-back-btn"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={2} />
              <span className="font-medium">Back</span>
            </button>
            <span className="text-sm font-semibold" style={{ color: BRAND.navy }}>
              VINdicated.<span style={{ color: BRAND.red }}>ai</span>
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ backgroundColor: '#FFFFFF' }}>
        <div className="max-w-screen-sm mx-auto px-5 py-10">
          
          {/* ==================== */}
          {/* HERO SECTION */}
          {/* ==================== */}
          <div 
            style={{
              background: 'linear-gradient(180deg, #001F3F 0%, #2A4A6B 100%)',
              borderRadius: '20px',
              padding: '16px 24px',
              marginBottom: '16px',
              textAlign: 'center',
            }}
          >
            <div className="flex items-center justify-center gap-3 mb-2">
              <div 
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Mail className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={1.75} />
              </div>
              <h1 className="text-lg font-bold tracking-tight" style={{ color: '#FFFFFF', letterSpacing: '-0.3px' }}>
                Out-the-Door Quote Emailer + Phone Script
              </h1>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: '#FFFFFF', lineHeight: '1.6' }}>
              Get real quotes without walking into the dealership blind. Everything you need to contact dealers like a pro — scripts, rebuttals, and strategies included.
            </p>
          </div>

          {/* Standalone callout badge */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <span 
              style={{ 
                display: 'inline-block',
                backgroundColor: '#FFFFFF', 
                color: '#FF4136',
                border: '2px solid #FF4136',
                borderRadius: '999px',
                padding: '10px 24px',
                fontWeight: '700',
                fontSize: '15px',
              }}
            >
              90% of your negotiation should happen before you visit the dealership
            </span>
          </div>

          {/* ==================== */}
          {/* SECTION A HEADER */}
          {/* ==================== */}
          <div style={{ marginBottom: '24px' }}>
            <h2 className="text-lg font-bold" style={{ color: BRAND.charcoal }}>Everything You Need — Ready to Use</h2>
            <p className="text-sm" style={{ color: '#6B7280' }}>No form required. Copy any of these right now.</p>
          </div>

          {/* ==================== */}
          {/* A1. PHONE SCRIPT */}
          {/* ==================== */}
          <div style={cardStyle(BRAND.navy)}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div style={{ width: 40, height: 40, borderRadius: '8px', background: 'linear-gradient(180deg, #001F3F 0%, #2A4A6B 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Phone className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                </div>
                <span className="font-bold" style={{ color: BRAND.navy }}>Phone Script — Call the Dealer</span>
              </div>
              <span className="px-2.5 py-1 text-xs font-semibold rounded-full" style={{ backgroundColor: '#FFFFFF', color: BRAND.red, border: `1px solid ${BRAND.red}` }}>
                Most effective method
              </span>
            </div>
            
            <div className="p-4 rounded-lg mb-5" style={{ backgroundColor: BRAND.lightGray }}>
              <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: BRAND.charcoal }}>{phoneScript}</p>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <Headset className="w-4 h-4" style={{ color: BRAND.navy }} strokeWidth={2} />
              <span className="font-bold text-sm" style={{ color: BRAND.charcoal }}>Tips to Make This Call Count</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0px', marginBottom: '16px' }}>
              {phoneTips.map((tip, i) => {
                // Icons for each tip: Calendar, User, ClipboardList (was Pencil), Phone, Lock (red for last one)
                const tipIcons = [
                  { icon: Calendar, gradient: 'linear-gradient(180deg, #001F3F 0%, #2A4A6B 100%)' },
                  { icon: User, gradient: 'linear-gradient(180deg, #001F3F 0%, #2A4A6B 100%)' },
                  { icon: ClipboardList, gradient: 'linear-gradient(180deg, #001F3F 0%, #2A4A6B 100%)' },
                  { icon: Phone, gradient: 'linear-gradient(180deg, #001F3F 0%, #2A4A6B 100%)' },
                  { icon: Lock, gradient: 'linear-gradient(180deg, #CC3333 0%, #FF4136 100%)' }, // Red for warning tip
                ];
                const IconComponent = tipIcons[i].icon;
                return (
                  <div key={i} style={miniCardStyle}>
                    <div className="flex items-start" style={{ gap: '12px' }}>
                      <div style={{ width: 32, height: 32, borderRadius: '8px', background: tipIcons[i].gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <IconComponent className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                      </div>
                      <span className="text-sm" style={{ color: BRAND.charcoal }}>{tip}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              onClick={() => copyToClipboard(phoneScript, 'phone')}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all w-full"
              style={{ backgroundColor: copiedItem === 'phone' ? BRAND.navy : '#FFFFFF', color: copiedItem === 'phone' ? '#FFFFFF' : BRAND.navy, border: `1px solid ${BRAND.navy}` }}
            >
              {copiedItem === 'phone' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copiedItem === 'phone' ? 'Copied!' : 'Copy Phone Script'}
            </button>
          </div>

          {/* ==================== */}
          {/* A2. OBJECTION HANDLERS */}
          {/* ==================== */}
          <div style={cardStyle(BRAND.red)}>
            <div className="flex items-center gap-3 mb-2">
              <div style={{ width: 40, height: 40, borderRadius: '8px', background: 'linear-gradient(180deg, #CC3333 0%, #FF4136 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <MessageCircleWarning className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
              </div>
              <span className="font-bold" style={{ color: BRAND.charcoal }}>When They Push Back</span>
            </div>
            <p className="text-sm mb-4" style={{ color: '#6B7280' }}>Dealers have scripts too. Here are yours.</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
              {objectionHandlers.map((obj, i) => {
                // Icons for each handler: Car, BarChart3, Tag, DollarSign, Handshake
                const handlerIcons = [Car, BarChart3, Tag, DollarSign, Handshake];
                const IconComponent = handlerIcons[i];
                return (
                  <div key={i} style={miniCardStyle}>
                    <div className="flex items-center gap-2 mb-2">
                      <IconComponent className="w-4 h-4" style={{ color: BRAND.charcoal }} strokeWidth={2} />
                      <p className="font-bold text-sm" style={{ color: BRAND.charcoal }}>{obj.title}</p>
                    </div>
                    <p className="text-xs font-medium mb-1" style={{ color: BRAND.red }}>Your response:</p>
                    <p className="text-sm" style={{ color: BRAND.charcoal }}>{obj.response}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ==================== */}
          {/* A3. LEAD FORM */}
          {/* ==================== */}
          <div style={cardStyle(BRAND.charcoal)}>
            <div className="flex items-center gap-3 mb-2">
              <div style={{ width: 40, height: 40, borderRadius: '8px', background: 'linear-gradient(180deg, #001F3F 0%, #2A4A6B 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Laptop className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
              </div>
              <span className="font-bold" style={{ color: BRAND.charcoal }}>Dealer Website Lead Form</span>
            </div>
            <p className="text-sm mb-4" style={{ color: '#6B7280' }}>Paste this into any dealer's online contact form</p>
            
            <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: BRAND.lightGray }}>
              <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: BRAND.charcoal }}>{leadFormText}</p>
            </div>

            <div style={{ ...miniCardStyle, marginBottom: '16px' }}>
              <p className="text-sm" style={{ color: BRAND.charcoal }}>
                <strong>Tip:</strong> Submitting through the dealer's own website often gets faster responses. Leads from a dealership's site typically get higher priority than leads from third-party sites like AutoTrader or Cars.com.
              </p>
            </div>

            <button
              onClick={() => copyToClipboard(leadFormText, 'leadform')}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all w-full"
              style={{ backgroundColor: copiedItem === 'leadform' ? BRAND.charcoal : '#FFFFFF', color: copiedItem === 'leadform' ? '#FFFFFF' : BRAND.charcoal, border: `1px solid ${BRAND.charcoal}` }}
            >
              {copiedItem === 'leadform' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copiedItem === 'leadform' ? 'Copied!' : 'Copy Lead Form Text'}
            </button>
          </div>

          {/* ==================== */}
          {/* A4. STRATEGY TIP CARDS */}
          {/* ==================== */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 31, 63, 0.08)', padding: '20px', textAlign: 'center' }}>
              <div style={{ width: 48, height: 48, borderRadius: '12px', background: 'linear-gradient(180deg, #001F3F 0%, #2A4A6B 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <Users className="w-6 h-6" style={{ color: '#FFFFFF' }} strokeWidth={1.5} />
              </div>
              <p className="font-bold text-sm mb-1" style={{ color: BRAND.charcoal }}>Contact at Least 5 Dealers</p>
              <p className="text-xs" style={{ color: '#6B7280' }}>More quotes means stronger leverage. Dealers can tell when they're your only option — and they price accordingly.</p>
            </div>
            <div style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 31, 63, 0.08)', padding: '20px', textAlign: 'center' }}>
              <div style={{ width: 48, height: 48, borderRadius: '12px', background: 'linear-gradient(180deg, #001F3F 0%, #2A4A6B 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <Globe className="w-6 h-6" style={{ color: '#FFFFFF' }} strokeWidth={1.5} />
              </div>
              <p className="font-bold text-sm mb-1" style={{ color: BRAND.charcoal }}>Use Their Own Website</p>
              <p className="text-xs" style={{ color: '#6B7280' }}>Leads from a dealer's own site get higher priority in their system than leads from AutoTrader or Cars.com.</p>
            </div>
            <div style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 31, 63, 0.08)', padding: '20px', textAlign: 'center' }}>
              <div style={{ width: 48, height: 48, borderRadius: '12px', background: 'linear-gradient(180deg, #CC3333 0%, #FF4136 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <FileText className="w-6 h-6" style={{ color: '#FFFFFF' }} strokeWidth={1.5} />
              </div>
              <p className="font-bold text-sm mb-1" style={{ color: BRAND.charcoal }}>Get Everything in Writing</p>
              <p className="text-xs" style={{ color: '#6B7280' }}>Verbal quotes are meaningless. Written quotes are much easier to compare and challenge if numbers change. Always ask for it in writing.</p>
            </div>
          </div>

          {/* ==================== */}
          {/* A5. PSYCHOLOGY */}
          {/* ==================== */}
          <div style={cardStyle(BRAND.navy)}>
            <div className="flex items-center gap-3 mb-2">
              <div style={{ width: 40, height: 40, borderRadius: '12px', background: 'linear-gradient(135deg, #001F3F, #1E3A8A)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
                <Brain className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
              </div>
              <span className="font-bold" style={{ color: BRAND.navy }}>Why This Works — The Psychology</span>
            </div>
            <p className="text-sm mb-4" style={{ color: BRAND.charcoal }}>Understanding these gives you an edge most buyers don't have</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
              {psychologyInsights.map((insight, i) => {
                // Icons: Anchor, Users, FileText, Hourglass - dark gradient background
                const insightIcons = [Anchor, Users, FileText, Hourglass];
                const IconComponent = insightIcons[i];
                return (
                  <div key={i} style={miniCardStyle}>
                    <div className="flex items-start" style={{ gap: '12px' }}>
                      <div style={{ width: 32, height: 32, borderRadius: '12px', background: 'linear-gradient(135deg, #001F3F, #1E3A8A)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: '10px' }}>
                        <IconComponent className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                      </div>
                      <div>
                        <p className="font-bold text-sm mb-1" style={{ color: BRAND.charcoal }}>{insight.title}</p>
                        <p className="text-sm" style={{ color: BRAND.charcoal }}>{insight.body}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ==================== */}
          {/* A6. PRO MOVES */}
          {/* ==================== */}
          <div style={cardStyle(BRAND.red)}>
            <div className="flex items-center gap-3 mb-2">
              <span className="font-bold" style={{ color: BRAND.charcoal }}>VINdicated Pro Moves</span>
            </div>
            <p className="text-sm mb-4" style={{ color: '#6B7280' }}>The stuff most buyers learn the hard way</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
              {proMoves.map((tip, i) => (
                <div key={i} style={miniCardStyle}>
                  <div className="flex items-start" style={{ gap: '12px' }}>
                    <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: '#FFFFFF', color: '#FF4136', border: '2px solid #FF4136' }}>
                      {i + 1}
                    </span>
                    <span className="text-sm" style={{ color: BRAND.charcoal }}>{tip}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ==================== */}
          {/* SECTION B HEADER */}
          {/* ==================== */}
          <div style={{ marginBottom: '24px', marginTop: '40px' }}>
            <h2 className="text-lg font-bold" style={{ color: BRAND.charcoal }}>Personalize Your Email</h2>
            <p className="text-sm" style={{ color: '#6B7280' }}>Fill in your details and we'll generate a professional quote request email ready to send to any dealer.</p>
          </div>

          {/* ==================== */}
          {/* FORM */}
          {/* ==================== */}
          <div style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 31, 63, 0.08)', padding: '28px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>Year</label>
                  <input type="text" placeholder="2024" value={emailData.year} onChange={(e) => setEmailData({...emailData, year: e.target.value})} className={inputClassName} style={inputStyle} data-testid="otd-year-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>Make</label>
                  <input type="text" placeholder="Toyota" value={emailData.make} onChange={(e) => setEmailData({...emailData, make: e.target.value})} className={inputClassName} style={inputStyle} data-testid="otd-make-input" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>Model</label>
                  <input type="text" placeholder="Camry" value={emailData.model} onChange={(e) => setEmailData({...emailData, model: e.target.value})} className={inputClassName} style={inputStyle} data-testid="otd-model-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>Trim (optional)</label>
                  <input type="text" placeholder="SE" value={emailData.trim} onChange={(e) => setEmailData({...emailData, trim: e.target.value})} className={inputClassName} style={inputStyle} data-testid="otd-trim-input" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>VIN (optional)</label>
                <input type="text" placeholder="1HGBH41JXMN109186" value={emailData.vin} onChange={(e) => setEmailData({...emailData, vin: e.target.value})} className={`${inputClassName} font-mono`} style={inputStyle} data-testid="otd-vin-input" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>Your ZIP Code</label>
                <input type="text" placeholder="90210" value={emailData.zip} onChange={(e) => setEmailData({...emailData, zip: e.target.value})} className={inputClassName} style={inputStyle} data-testid="otd-zip-input" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>Purchase Timeline</label>
                <select value={emailData.purchaseTimeline} onChange={(e) => setEmailData({...emailData, purchaseTimeline: e.target.value})} className={inputClassName} style={inputStyle} data-testid="otd-timeline-select">
                  <option value="">Select timeline...</option>
                  <option value="Ready to buy within 3 days">Ready to buy within 3 days</option>
                  <option value="Ready to buy this week">Ready to buy this week</option>
                  <option value="Buying within 2 weeks">Buying within 2 weeks</option>
                  <option value="Buying within 30 days">Buying within 30 days</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>Preferred Contact Method (optional)</label>
                <select value={emailData.preferredContact} onChange={(e) => setEmailData({...emailData, preferredContact: e.target.value})} className={inputClassName} style={inputStyle} data-testid="otd-contact-select">
                  <option value="">Select preference...</option>
                  <option value="Email only">Email only</option>
                  <option value="Email or text">Email or text</option>
                  <option value="Phone call OK">Phone call OK</option>
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>Name</label>
                  <input type="text" placeholder="Your name" value={emailData.buyerName} onChange={(e) => setEmailData({...emailData, buyerName: e.target.value})} className={inputClassName} style={inputStyle} data-testid="otd-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>Phone (optional)</label>
                  <input type="text" placeholder="Phone number" value={emailData.buyerPhone} onChange={(e) => setEmailData({...emailData, buyerPhone: e.target.value})} className={inputClassName} style={inputStyle} data-testid="otd-phone-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>Email (optional)</label>
                  <input type="email" placeholder="your@email.com" value={emailData.buyerEmail} onChange={(e) => setEmailData({...emailData, buyerEmail: e.target.value})} className={inputClassName} style={inputStyle} data-testid="otd-email-input" />
                </div>
              </div>

              <div style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', padding: '16px', border: '1px solid #E5E7EB' }}>
                <p className="text-sm font-medium" style={{ color: BRAND.navy }}>Recommended: send this request to at least five dealerships.</p>
                <p className="text-sm" style={{ color: '#6B7280' }}>More quotes = stronger negotiating leverage.</p>
              </div>

              <button onClick={generateOTDEmail} className="w-full py-3.5 font-semibold text-sm rounded-lg transition-all flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.99]" style={{ background: 'linear-gradient(180deg, #001F3F 0%, #0A4A7A 100%)', color: '#FFFFFF', boxShadow: '0 4px 12px rgba(0,31,63,0.20)' }} data-testid="otd-generate-btn">
                <span>Generate Email</span>
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </div>
          </div>

          {/* ==================== */}
          {/* GENERATED EMAIL */}
          {/* ==================== */}
          {generatedEmail && (
            <>
              <div style={cardStyle(BRAND.navy)}>
                <p className="text-xs font-semibold mb-3 uppercase" style={{ color: '#6B7280' }}>Generated Email Template</p>
                <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: BRAND.lightGray, border: '1px solid #E5E7EB' }}>
                  <pre className="text-xs whitespace-pre-wrap font-mono leading-relaxed" style={{ color: '#374151' }} data-testid="otd-generated-email">{generatedEmail}</pre>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <button onClick={() => copyToClipboard(generatedEmail, 'email')} className="py-2.5 font-semibold text-sm rounded-lg transition-all flex items-center justify-center gap-1" style={{ backgroundColor: copiedItem === 'email' ? BRAND.navy : '#FFFFFF', color: copiedItem === 'email' ? '#FFFFFF' : BRAND.navy, border: `1px solid ${BRAND.navy}` }} data-testid="otd-copy-email-btn">
                    {copiedItem === 'email' ? 'Copied!' : 'Copy Email'}
                  </button>
                  <button onClick={() => window.open(`https://mail.google.com/mail/?view=cm&body=${encodeURIComponent(generatedEmail)}`, '_blank')} className="py-2.5 font-semibold text-sm rounded-lg transition-all" style={{ backgroundColor: '#FFFFFF', color: BRAND.navy, border: `1px solid ${BRAND.navy}` }} data-testid="otd-gmail-btn">
                    Open in Gmail
                  </button>
                  <button onClick={() => window.open(`https://outlook.live.com/mail/0/deeplink/compose?body=${encodeURIComponent(generatedEmail)}`, '_blank')} className="py-2.5 font-semibold text-sm rounded-lg transition-all" style={{ backgroundColor: '#FFFFFF', color: BRAND.navy, border: `1px solid ${BRAND.navy}` }} data-testid="otd-outlook-btn">
                    Open in Outlook
                  </button>
                </div>
              </div>

              <div style={cardStyle(BRAND.charcoal)}>
                <div className="flex items-center gap-3 mb-3">
                  <Lightbulb className="w-5 h-5" style={{ color: BRAND.charcoal }} strokeWidth={2} />
                  <span className="font-bold" style={{ color: BRAND.charcoal }}>What Happens Next?</span>
                </div>
                {whatHappensNext.map((item, i) => (
                  <div key={i} style={miniCardStyle}>
                    <div className="flex items-start gap-2">
                      <span style={{ color: BRAND.charcoal, fontWeight: 'bold', marginTop: '4px', fontSize: '8px' }}>●</span>
                      <span className="text-sm" style={{ color: BRAND.charcoal }}>{item}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div style={{ height: '60px' }}></div>
        </div>
      </div>
    </div>
  );
};

export default OTDEmailerPage;
